import { forwardRef } from "react";

import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import TextStyle, { TextStyleOptions } from "@tiptap/extension-text-style";
import { EditorContent, generateHTML, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

import { WyswygToolbar } from "./wyswyg-toolbar";

const extensions = [
  TextStyle.configure({ types: [ListItem.name] } as Partial<TextStyleOptions>),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
  }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    defaultProtocol: "https",
    protocols: ["http", "https", "mailto", "tel"],
  }),
];

type TiptapWyswygProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  disabled?: boolean;
};

const TiptapWyswyg = forwardRef<HTMLDivElement, TiptapWyswygProps>(
  (props, ref) => {
    const { value, onChange, onBlur, disabled } = props;

    const editor = useEditor({
      extensions,
      content: value,
      editable: !disabled,
      editorProps: {
        attributes: {
          class:
            "min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 prose dark:prose-invert prose-sm focus:outline-none",
        },
      },
      onUpdate({ editor }) {
        onChange(generateHTML(editor.getJSON(), [StarterKit, Link, TextStyle]));
      },
      onBlur,
    });

    if (!editor) {
      return null;
    }

    return (
      <div className="space-y-3">
        <WyswygToolbar editor={editor} />
        <EditorContent editor={editor} ref={ref} />
      </div>
    );
  },
);

TiptapWyswyg.displayName = "TiptapWyswyg";

export default TiptapWyswyg;
